/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {AgGridType} from './IonGrid';

export interface useGridDeselectOnMouseOut {
  gridApi: AgGridType;
  onMouseOut: Function;
};

/**
 *
 * Provides a Hook to deselect the grid row if already selected 
 * and it will execute the callback function when the mouse leaves the row.
 *
 * ```typescript
 *
 *    const [hoverRef] = useGridDeselectOnMouseOut(gridApi, onMouseOut);
 *
 *    <div ref={hoverRef} ... />
 *      <IonGrid ... />
 *    </div>
 *
 * ```
 */
export const useGridDeselectOnMouseOut = (gridApi: AgGridType, onMouseOut: Function) => {
  const [isMouseOut, setIsMouseOut] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = nodeRef.current;
    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);
      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
  }, [nodeRef.current]);

  useEffect(() => {
    if (gridApi && onMouseOut && isMouseOut) {
      gridApi.deselectAll?.();
      onMouseOut();
    }
  }, [isMouseOut]);

  const handleMouseOver = useCallback(() => setIsMouseOut(false), []);
  const handleMouseOut = useCallback(() => setIsMouseOut(true), []);

  return [nodeRef];
};

export default useGridDeselectOnMouseOut;
